import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
    OidcChooseBody,
    OkResponse,
    OwnerCheckVerifyBody,
    OwnerCheckView,
    PartnerJoinBody,
    PartnerJoinPreview,
    PartnerPendingState,
    PartnerSignInResult,
} from "@mfa-aff/shared";
import {
    joinMock,
    joinPreviewMock,
    logoutMock,
    oidcChooseMock,
    oidcStartMock,
    ownerCheckMock,
    ownerCheckResendMock,
    ownerCheckVerifyMock,
    pendingMock,
} from "../mocks/auth";
import { get, send } from "./client";
import { USE_MOCKS } from "./config";
import { meKey } from "./me";
import { partnerPaths } from "./paths";

/**
 * Partner sign-in (design doc §13, apps/api README «Partner sign-in»): only the Mizbanfa account
 * (WHMCS OpenID Connect, a full-page navigation). WHMCS is the single identity: there is no
 * mobile/SMS sign-in and no signup form. A WHMCS client without a partner account activates one on
 * `/join`. What the OIDC round trip proved (client or chooser list) lives in the httpOnly
 * `__Host-mzp_pending` cookie; the panel reads it back with `GET auth/pending` / `GET auth/join`.
 */

/** Where Mizbanfa customers sign up (a customer account comes first, then marketing). */
export const WHMCS_REGISTER_URL = "https://my.mizbanfa.net/register.php";
/** Where name, mobile and e-mail are edited. */
export const WHMCS_PROFILE_URL = "https://my.mizbanfa.net/clientarea.php?action=details";

export const pendingKey = ["partner", "auth", "pending"] as const;
export const ownerCheckKey = ["partner", "auth", "owner-check"] as const;
export const joinKey = ["partner", "auth", "join"] as const;

/** `GET auth/pending`: WHMCS client chooser list, rules version in force. */
export const usePending = (enabled = true) =>
    useQuery({
        queryKey: pendingKey,
        queryFn: () => get<PartnerPendingState>(partnerPaths.authPending, () => pendingMock()),
        enabled,
        staleTime: 0,
    });

/** `GET auth/join`: the WHMCS client data activation will copy, and the rule book in force. */
export const useJoinPreview = () =>
    useQuery({
        queryKey: joinKey,
        queryFn: () => get<PartnerJoinPreview>(partnerPaths.join, () => joinPreviewMock()),
        staleTime: 0,
        retry: false,
    });

/** After a sign-in result: refresh `/me` (signed in) or the pending state (join next). */
const useSettle = () => {
    const qc = useQueryClient();
    return (r: PartnerSignInResult) => {
        if (r.status === "signed_in") qc.setQueryData(meKey, r.me);
        void qc.invalidateQueries({ queryKey: r.status === "signed_in" ? meKey : pendingKey });
    };
};

/**
 * «ورود با حساب میزبان‌فا». Live: a full-page navigation to `GET auth/oidc/start` (302 to WHMCS, back
 * through the API callback to `/auth/callback?status=…`). Mocks: the round trip is simulated and the
 * returned panel path (`/auth/callback?status=…`) is where to go next.
 */
export function startOidc(navigate: (to: string) => void): void {
    if (!USE_MOCKS) {
        window.location.assign(partnerPaths.oidcStart);
        return;
    }
    navigate(oidcStartMock());
}

export const useOidcChoose = () => {
    const settle = useSettle();
    return useMutation({
        mutationFn: (body: OidcChooseBody) => send<PartnerSignInResult>("POST", partnerPaths.oidcChoose, body, () => oidcChooseMock(body)),
        onSuccess: settle,
    });
};

/**
 * `GET auth/owner-check`: a client account with several users signed in, so a code went by SMS to
 * that account's own mobile in Mizbanfa; where it went (masked) and the timers.
 */
export const useOwnerCheck = () =>
    useQuery({
        queryKey: ownerCheckKey,
        queryFn: () => get<OwnerCheckView>(partnerPaths.ownerCheck, () => ownerCheckMock()),
        staleTime: 0,
        retry: false,
        refetchOnWindowFocus: false,
    });

/** `POST auth/owner-check/resend`: a new code to the same mobile (never a typed number). */
export const useOwnerCheckResend = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: () => send<OwnerCheckView>("POST", partnerPaths.ownerCheckResend, undefined, () => ownerCheckResendMock()),
        meta: { silent: true },
        onSuccess: (v) => qc.setQueryData(ownerCheckKey, v),
    });
};

/** `POST auth/owner-check/verify`: the right code continues as the account owner (signed in, or on to `/join`). */
export const useOwnerCheckVerify = () => {
    const settle = useSettle();
    return useMutation({
        mutationFn: (body: OwnerCheckVerifyBody) => send<PartnerSignInResult>("POST", partnerPaths.ownerCheckVerify, body, () => ownerCheckVerifyMock(body)),
        meta: { silent: true },
        onSuccess: settle,
    });
};

/** `POST auth/join`: activate marketing for the proven WHMCS client and sign in. */
export const useJoin = () => {
    const settle = useSettle();
    return useMutation({
        mutationFn: (body: PartnerJoinBody) => send<PartnerSignInResult>("POST", partnerPaths.join, body, () => joinMock(body)),
        meta: { silent: true },
        onSuccess: settle,
    });
};

export const useLogout = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: () => send<OkResponse>("POST", partnerPaths.logout, undefined, () => logoutMock()),
        onSettled: () => qc.clear(),
    });
};
